import * as R from "ramda";

import {
  PromptTemplate,
  PromptTemplateCreationParams,
  PromptTemplateUpdateParams
} from "../../types/prompt-template";
import { TEMPERATURE_MAX, TEMPERATURE_MIN } from "./constants";

export type LensFormErrorKey =
  | "title"
  | "description"
  | "prompt"
  | "model_config.provider"
  | "model_config.model"
  | "model_config.temperature"
  | "model_config.max_tokens"
  | "metadata.category"
  | "metadata.about"
  | "metadata.audience"
  | "metadata.primary_function"
  | "metadata.scope"
  | "submit";

export type LensFormErrors = Partial<Record<LensFormErrorKey, string>>;

interface ValidationRule {
  key: LensFormErrorKey;
  message: string;
  isValid: boolean;
}

export const validateLensForm = (
  formData: PromptTemplateCreationParams
): LensFormErrors => {
  const rules: ValidationRule[] = [
    { key: "title", message: "Title is required", isValid: Boolean(formData.title.trim()) },
    { key: "description", message: "Description is required", isValid: Boolean(formData.description.trim()) },
    { key: "prompt", message: "Prompt is required", isValid: Boolean(formData.prompt.trim()) },
    { key: "model_config.provider", message: "Provider is required", isValid: Boolean(formData.model_config.provider.trim()) },
    { key: "model_config.model", message: "Model is required", isValid: Boolean(formData.model_config.model.trim()) },
    {
      key: "model_config.temperature",
      message: "Temperature must be between 0 and 1",
      isValid: formData.model_config.temperature >= TEMPERATURE_MIN && formData.model_config.temperature <= TEMPERATURE_MAX
    },
    { key: "model_config.max_tokens", message: "Max tokens must be greater than 0", isValid: formData.model_config.max_tokens > 0 },
    { key: "metadata.category", message: "Category is required", isValid: Boolean(formData.metadata.category) },
    { key: "metadata.about", message: "About field is required", isValid: Boolean(formData.metadata.about) },
    { key: "metadata.audience", message: "Audience is required", isValid: Boolean(formData.metadata.audience) },
    { key: "metadata.primary_function", message: "Primary function is required", isValid: Boolean(formData.metadata.primary_function) },
    { key: "metadata.scope", message: "At least one scope must be selected", isValid: !R.isEmpty(formData.metadata.scope ?? []) }
  ];

  return rules.reduce<LensFormErrors>(
    (errors, rule) => rule.isValid ? errors : { ...errors, [rule.key]: rule.message },
    {}
  );
};

export const templateToFormData = (
  template: PromptTemplate
): PromptTemplateCreationParams =>
  R.omit(["id", "created", "modified"], template) as PromptTemplateCreationParams;

export const buildTemplateDiff = (
  original: PromptTemplate,
  formData: PromptTemplateCreationParams
): PromptTemplateUpdateParams => {
  const baseData = templateToFormData(original);
  const updateKeys: (keyof PromptTemplateUpdateParams)[] = [
    "title", "description", "prompt", "enabled", "model_config", "metadata"
  ];

  return updateKeys.reduce<PromptTemplateUpdateParams>(
    (diff, key) => R.equals(baseData[key], formData[key])
      ? diff
      : { ...diff, [key]: formData[key] },
    {}
  );
};

interface SaveTemplateParams {
  template: PromptTemplate | null;
  formData: PromptTemplateCreationParams;
  createTemplate: (data: PromptTemplateCreationParams) => Promise<unknown>;
  updateTemplate: (id: string, data: PromptTemplateUpdateParams) => Promise<unknown>;
}

export const saveTemplate = async ({
  template, formData, createTemplate, updateTemplate
}: SaveTemplateParams): Promise<void> => {
  if (!template) {
    await createTemplate(formData);
    return;
  }
  await updateTemplate(template.id, buildTemplateDiff(template, formData));
};

export const resolveInitialFormData = (
  template: PromptTemplate | null,
  defaultTemplate: PromptTemplateCreationParams
): PromptTemplateCreationParams => {
  if (!template) {
    return defaultTemplate;
  }
  return templateToFormData(template);
};

export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
};
